import { useState, useEffect, useCallback } from 'react';
import { Trophy, Calendar, Users, Plus, X, Check, Pin, Award, Heart, ExternalLink, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BrandingChallenges = ({ user, isAdmin }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(null);
  const [expandedChallenge, setExpandedChallenge] = useState(null);
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    requirements: '',
    start_date: '',
    end_date: '',
    is_pinned: true
  });
  
  // Submit form state
  const [submitForm, setSubmitForm] = useState({
    content: '',
    proof_url: '',
    proof_image_url: ''
  });

  const fetchChallenges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/challenges`);
      setChallenges(response.data || []);
    } catch (error) {
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  const handleCreateChallenge = async () => {
    if (!createForm.title || !createForm.description || !createForm.requirements) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/challenges`, createForm);
      toast.success('Challenge created!');
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        description: '',
        requirements: '',
        start_date: '',
        end_date: '',
        is_pinned: true
      });
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create challenge');
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/join`);
      toast.success('Joined challenge!');
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join challenge');
    }
  };

  const handleSubmitProof = async (challengeId) => {
    if (!submitForm.content) {
      toast.error('Please describe your completion');
      return;
    }
    
    try {
      await axios.post(`${API}/challenges/${challengeId}/submit`, submitForm);
      toast.success('Submission posted!');
      setShowSubmitModal(null);
      setSubmitForm({ content: '', proof_url: '', proof_image_url: '' });
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleLikeSubmission = async (challengeId, userId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/submissions/${userId}/like`);
      fetchChallenges();
    } catch (error) {
      toast.error('Failed to like submission');
    }
  };

  const handleSelectMVP = async (challengeId, userId) => {
    if (!window.confirm('Select this user as the Branding MVP of the Month?')) return;
    
    try {
      await axios.put(`${API}/challenges/${challengeId}`, { mvp_user_id: userId });
      toast.success('MVP selected! Badge awarded.');
      fetchChallenges();
    } catch (error) {
      toast.error('Failed to select MVP');
    }
  };

  const handleTogglePin = async (challengeId, currentPinned) => {
    try {
      await axios.put(`${API}/challenges/${challengeId}`, { is_pinned: !currentPinned });
      fetchChallenges();
    } catch (error) {
      toast.error('Failed to update challenge');
    }
  };

  const handleEndChallenge = async (challengeId) => {
    if (!window.confirm('End this challenge?')) return;
    
    try {
      await axios.put(`${API}/challenges/${challengeId}`, { is_active: false });
      toast.success('Challenge ended');
      fetchChallenges();
    } catch (error) {
      toast.error('Failed to end challenge');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branding-challenges">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Trophy size={24} style={{ color: '#D4AF37' }} />
            Monthly Branding Challenges
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Complete challenges to grow your brand and earn MVP status!
          </p>
        </div>
        
        {isAdmin && (
          <Button
            onClick={() => setShowCreateModal(true)}
            className="rounded-full"
            style={{ background: '#D4AF37', color: '#000' }}
            data-testid="create-challenge-btn"
          >
            <Plus size={16} className="mr-1" />
            Create Challenge
          </Button>
        )}
      </div>

      {/* Challenges List */}
      {challenges.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Trophy size={48} className="mx-auto mb-4 opacity-50" />
          <p>No active challenges yet.</p>
          {isAdmin && <p className="text-sm mt-2">Create one to get started!</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {challenges.map((challenge) => (
            <div 
              key={challenge.id}
              className={`rounded-xl p-5 ${challenge.is_pinned ? 'ring-2 ring-[#D4AF37]/50' : ''}`}
              style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.2)' }}
            >
              {/* Challenge Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {challenge.is_pinned && (
                    <Pin size={16} style={{ color: '#D4AF37' }} />
                  )}
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: '#D4AF37' }}>{challenge.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(challenge.start_date)} - {formatDate(challenge.end_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {challenge.participant_count || 0} participants
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Admin Controls */}
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleTogglePin(challenge.id, challenge.is_pinned)}
                      style={{ color: challenge.is_pinned ? '#D4AF37' : '#666' }}
                    >
                      <Pin size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEndChallenge(challenge.id)}
                      style={{ color: '#ef4444' }}
                    >
                      End
                    </Button>
                  </div>
                )}
              </div>

              {/* Description */}
              <p className="text-gray-300 mb-3">{challenge.description}</p>

              {/* Requirements */}
              <div className="p-3 rounded-lg mb-4" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <p className="text-sm font-semibold mb-1" style={{ color: '#D4AF37' }}>Requirements:</p>
                <p className="text-sm text-gray-300">{challenge.requirements}</p>
              </div>

              {/* MVP Banner */}
              {challenge.mvp_user_id && (
                <div className="p-3 rounded-lg mb-4 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2), rgba(251, 191, 36, 0.1))', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
                  <Trophy size={20} className="text-yellow-500" />
                  <span className="text-sm font-bold text-yellow-500">
                    🏆 MVP of the Month: {challenge.submissions?.find(s => s.user_id === challenge.mvp_user_id)?.user_name || 'Winner'}
                  </span>
                </div>
              )}

              {/* Join / Submit Actions */}
              <div className="flex items-center gap-3 mb-4">
                {!challenge.user_joined ? (
                  <Button
                    onClick={() => handleJoinChallenge(challenge.id)}
                    className="rounded-full"
                    style={{ background: '#D4AF37', color: '#000' }}
                    data-testid={`join-challenge-${challenge.id}`}
                  >
                    <Plus size={16} className="mr-1" />
                    Join Challenge
                  </Button>
                ) : challenge.user_submission ? (
                  <span className="flex items-center gap-2 px-3 py-2 rounded-full text-sm bg-green-500/20 text-green-500">
                    <Check size={16} />
                    Submitted
                  </span>
                ) : (
                  <Button
                    onClick={() => setShowSubmitModal(challenge.id)}
                    className="rounded-full"
                    variant="outline"
                    style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                    data-testid={`submit-challenge-${challenge.id}`}
                  >
                    <Send size={16} className="mr-1" />
                    Post Your Proof
                  </Button>
                )}
              </div>

              {/* Submissions Toggle */}
              {challenge.submissions?.length > 0 && (
                <button
                  onClick={() => setExpandedChallenge(expandedChallenge === challenge.id ? null : challenge.id)}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {expandedChallenge === challenge.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {challenge.submissions.length} Submission{challenge.submissions.length !== 1 ? 's' : ''}
                </button>
              )}

              {/* Submissions List */}
              {expandedChallenge === challenge.id && challenge.submissions?.length > 0 && (
                <div className="mt-4 space-y-3 pt-4 border-t border-white/10">
                  {challenge.submissions.map((sub, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg ${sub.user_id === challenge.mvp_user_id ? 'ring-2 ring-yellow-500/50' : ''}`}
                      style={{ background: 'rgba(255, 255, 255, 0.05)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                            style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
                          >
                            {(sub.user_name || 'U').charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{sub.user_name}</p>
                            <p className="text-xs text-gray-500">{formatDate(sub.submission?.submitted_at)}</p>
                          </div>
                          {sub.user_id === challenge.mvp_user_id && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-500">🏆 MVP</span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Like button */}
                          <button
                            onClick={() => handleLikeSubmission(challenge.id, sub.user_id)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                              sub.submission?.likes?.includes(user?.id) 
                                ? 'bg-red-500/20 text-red-500' 
                                : 'hover:bg-white/10'
                            }`}
                          >
                            <Heart size={14} fill={sub.submission?.likes?.includes(user?.id) ? 'currentColor' : 'none'} />
                            <span>{sub.submission?.likes?.length || 0}</span>
                          </button>
                          
                          {/* Admin MVP selection */}
                          {isAdmin && !challenge.mvp_user_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSelectMVP(challenge.id, sub.user_id)}
                              style={{ color: '#D4AF37' }}
                              title="Select as MVP"
                            >
                              <Award size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-300">{sub.submission?.content}</p>
                      
                      {/* Proof links */}
                      {(sub.submission?.proof_url || sub.submission?.proof_image_url) && (
                        <div className="flex gap-2 mt-2">
                          {sub.submission?.proof_url && (
                            <a 
                              href={sub.submission.proof_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                            >
                              <ExternalLink size={12} />
                              View Proof
                            </a>
                          )}
                          {sub.submission?.proof_image_url && (
                            <a 
                              href={sub.submission.proof_image_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            >
                              <ExternalLink size={12} />
                              View Image
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Challenge Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowCreateModal(false)}>
          <div 
            className="rounded-2xl p-6 border max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Create New Challenge</h3>
              <button onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Challenge Title *</Label>
                <Input
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  placeholder="e.g., February Brand Refresh"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Describe the challenge..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Requirements *</Label>
                <Textarea
                  value={createForm.requirements}
                  onChange={(e) => setCreateForm({ ...createForm, requirements: e.target.value })}
                  placeholder="What participants need to do to complete the challenge..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={createForm.start_date}
                    onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={createForm.end_date}
                    onChange={(e) => setCreateForm({ ...createForm, end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pin-challenge"
                  checked={createForm.is_pinned}
                  onChange={(e) => setCreateForm({ ...createForm, is_pinned: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="pin-challenge" className="cursor-pointer">Pin this challenge</Label>
              </div>
              
              <Button
                onClick={handleCreateChallenge}
                className="w-full rounded-full"
                style={{ background: '#D4AF37', color: '#000' }}
              >
                Create Challenge
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Proof Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowSubmitModal(null)}>
          <div 
            className="rounded-2xl p-6 border max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Submit Your Proof</h3>
              <button onClick={() => setShowSubmitModal(null)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Describe Your Completion *</Label>
                <Textarea
                  value={submitForm.content}
                  onChange={(e) => setSubmitForm({ ...submitForm, content: e.target.value })}
                  placeholder="Share what you did to complete this challenge..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Proof Link (optional)</Label>
                <Input
                  value={submitForm.proof_url}
                  onChange={(e) => setSubmitForm({ ...submitForm, proof_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Image URL (optional)</Label>
                <Input
                  value={submitForm.proof_image_url}
                  onChange={(e) => setSubmitForm({ ...submitForm, proof_image_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
              
              <Button
                onClick={() => handleSubmitProof(showSubmitModal)}
                className="w-full rounded-full"
                style={{ background: '#D4AF37', color: '#000' }}
              >
                <Send size={16} className="mr-1" />
                Post Submission
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
